/**
 * useDataCache.js
 * 
 * A stale-while-revalidate cache for API data.
 * The cache Map lives at module level (outside React), so it persists across
 * route changes for the lifetime of the browser session.
 *
 * Behaviour:
 *  - First visit to a page: fetches normally, shows loading=true
 *  - Return visit:          instantly shows cached data (loading=false),
 *                           then silently re-fetches in background so data stays fresh
 *
 * SignalR real-time updates are NOT affected — they push directly into component
 * state just as before. This cache only handles initial page-entry data.
 */

import { useState, useEffect, useRef } from 'react';

// Module-level cache — persists across navigation, cleared on hard refresh
const cache = new Map();

/**
 * @param {string}   cacheKey  A unique key for this data set (e.g. "gateways-p1-q-")
 * @param {Function} fetcher   Async function that returns the data to cache
 * @param {Array}    deps      Dependency array — changing deps invalidates cache and re-fetches
 */
export function useDataCache(cacheKey, fetcher, deps = []) {
  const hasCache = cache.has(cacheKey);
  const [data, setData] = useState(hasCache ? cache.get(cacheKey) : null);
  const [loading, setLoading] = useState(!hasCache);
  const [error, setError] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    const isStale = cache.has(cacheKey);

    // If we have cached data, show it immediately — no white screen
    // Then still re-fetch in background so data is always fresh
    if (!isStale) {
      setLoading(true);
    }
    setError(null);

    (async () => {
      try {
        const result = await fetcher();
        if (!cancelledRef.current) {
          cache.set(cacheKey, result);
          setData(result);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelledRef.current) {
          setError(err);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ...deps]);

  /**
   * Call this to force-invalidate the cache entry and re-fetch immediately.
   * Use after create / update / delete mutations.
   */
  const invalidate = () => {
    cache.delete(cacheKey);
  };

  return { data, loading, error, invalidate };
}

/** Manually clear a specific cache entry (e.g. after a mutation) */
export function invalidateCache(cacheKey) {
  cache.delete(cacheKey);
}

/** Clear all cached data (e.g. on logout) */
export function clearAllCache() {
  cache.clear();
}
